import { isEmpty } from 'lodash';
import { buildUrlWithParams } from '../core/helper';

export const getTemplatesList = templates => {
    const returnTemplates = [];
    templates.forEach(template => {
        returnTemplates.push({ id: template.id, value: template.title });
    });
    return returnTemplates;
};

export const getTemplatesFromSkill = (templatesList, skill) => templatesList.filter(template => template.groups === skill);

// Finds the template matching the picked title and skill. Skills are compared as
// strings, exactly like the selector filters its options, so any picked option is found.
export const getTemplateFromTitleAndSkillId = (templatesList, title, skillId) => (!title
    ? null
    : templatesList.find(template => template.title === title
        && template.groups?.toString() === skillId?.toString()) || null);

export const getTemplateInstructionFromTitleAndSkillId = (templatesList, title, skillId) => getTemplateFromTitleAndSkillId(templatesList, title, skillId)?.instructions || '';

// Returns the external URL of an EXTERNAL_LINK template (null for other templates).
// Adds https:// when missing, so the browser cannot treat the link as a relative path.
export const getExternalRedirectUrl = template => {
    const customField = template?.custom_field;
    if (customField?.type !== 'EXTERNAL_LINK' || isEmpty(customField.external_link)) {
        return null;
    }
    const externalLink = customField.external_link;
    const isAbsoluteUrl = externalLink.startsWith('http://') || externalLink.startsWith('https://');
    return isAbsoluteUrl ? externalLink : `https://${externalLink}`;
};

export const getTemplatesIdsFromSkill = (templatesList, skill) => getTemplatesFromSkill(templatesList, skill).map(template => template.id);

export const shapeTemplatesListAsGroupsList = templatesList => templatesList.map(template => ({ group_name: template.title, group_id: template.id }));

// Adds `?merciyanisToken=…` to the destination URL, keeping its existing query/fragment.
// The prefixed name avoids overriding a `token` param the destination may already use.
// Throws on a malformed URL, so the caller can fail closed (no redirect).
export const appendTokenToUrl = (baseUrl, token) => {
    const url = new URL(baseUrl);
    url.searchParams.set('merciyanisToken', token);
    return url.toString();
};

// Fetches an opaque context token from the (unauthenticated) wos endpoint.
// The context ids are sent as GET query params; null/undefined ones are skipped.
// Throws on HTTP error, timeout (8s) or missing token, so the caller can fail closed.
export const fetchExternalLinkToken = async context => {
    const baseUrl = window.location.href.includes('merciyanis.io')
        ? 'https://wos.merciyanis.io'
        : 'https://wos.merciyanis.com';
    const workspace = window.location.hostname.split('.')[0];
    const url = buildUrlWithParams(`${baseUrl}/api/external-redirect-token`, context);
    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), 8000);
    try {
        const response = await fetch(url, {
            method: 'GET',
            headers: {
                'X-MerciYanis-Workspace': workspace,
                'X-Device-Id': 'APP'
            },
            signal: controller.signal
        });
        if (!response.ok) {
            throw new Error(`external-link token request failed with status ${response.status}`);
        }
        const data = await response.json();
        if (!data?.token) {
            throw new Error('external-link token missing from response');
        }
        return data.token;
    } finally {
        clearTimeout(timeout);
    }
};
